import { useState } from "react";
import LeistungCombo from "../LeistungCombo";
import NebenkostenBezug from "../NebenkostenBezug";
import { eur } from "../../hooks/useVertraege";
import { einheitLabel, einheitWert } from "../../lib/labels";
import { punkteIn, umschalten, maxJeMonat, istNebenkosten } from "../../lib/frequenz";
import { positionTitel } from "../../lib/wizard";
import type { KatalogEintrag, VertragPosition } from "../../lib/wizard";
import { fehlertext } from "../../lib/fehler";

const MONAT_KURZ = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/**
 * Eine Position im Vertrag — vollständig editierbar, auch der Titel.
 *
 * Der Katalog ist die Vorlage, nicht das Gesetz: was am Garten steht, weicht
 * regelmäßig davon ab („Heckenschnitt bis 2 m, aber im Hang"). Deshalb lassen
 * sich Titel, Einheit, Menge, Preis und — aufgeklappt — auch Kosten, Zeit und
 * Lohnanteil hier ändern, ohne dass jemand den Katalog anfassen muss. Die Werte
 * sind Schnappschüsse an der Position; eine spätere Katalogänderung fasst
 * bestehende Verträge nicht an.
 *
 * Steht ein Titel da, den der Katalog nicht kennt, wird angeboten, ihn
 * aufzunehmen — sonst wird dieselbe Leistung beim nächsten Kunden wieder von
 * Hand getippt.
 */
export default function PositionZeile({ p, positionen, katalog, faktor, onPatch, onRemove, onKatalogAufnehmen, aufnahmeLaeuft }: {
  p: VertragPosition; positionen: VertragPosition[]; katalog: KatalogEintrag[]; faktor: number;
  onPatch: (patch: Partial<VertragPosition>) => void; onRemove: () => void;
  onKatalogAufnehmen: () => Promise<void>; aufnahmeLaeuft: boolean;
}) {
  const [details, setDetails] = useState(false);
  const [fehler, setFehler] = useState("");
  const titel = positionTitel(p, katalog);
  const eff = Math.round(p.einzelpreis_netto * faktor * 100) / 100;
  const jeDurchgang = eff * p.menge;
  const gewaehlt = p.monate.length;
  const nebenkosten = istNebenkosten(p, katalog);
  // Folgt die Position ihren Leistungen, ist das Raster eine Anzeige, kein
  // Eingabefeld — ein Klick würde beim nächsten Nachziehen verpuffen.
  const rechnetSich = nebenkosten && !(p.folgt && p.folgt.length === 0);
  const grenze = maxJeMonat(p, katalog);

  // Abgleich über den Titel, nicht über leistung_id: auch eine umbenannte
  // Katalogposition ist eine Leistung, die der Katalog so nicht kennt.
  const gesucht = titel.trim().toLowerCase();
  const imKatalog = katalog.some(k => k.titel.trim().toLowerCase() === gesucht);
  const kannAufnehmen = gesucht !== "" && !imKatalog;

  const db = Math.round((p.einzelpreis_netto - p.kosten_snapshot) * 100) / 100;
  const katalogText = katalog.find(k => k.id === p.leistung_id)?.beschreibung?.trim() || "";

  return (
    <div className="pf-card pf-wz-position">
      <div className="pf-wz-position-kopf">
        <label className="pf-field pf-wz-feld pf-wz-wachsen">
          <span>Leistung</span>
          <LeistungCombo
            wert={titel}
            katalog={katalog}
            platzhalter="Bezeichnung der Leistung"
            onText={t => onPatch({ titel_override: t })}
            // Aus dem Katalog gewählt heißt: die ganze Leistung übernehmen,
            // nicht nur ihren Namen. Sonst stünde ein Katalogtitel über einem
            // fremden Preis. Die Menge bleibt, die hängt am Garten.
            onWaehlen={k => onPatch({
              leistung_id: k.id, titel_override: null, einheit: k.einheit,
              einzelpreis_netto: Number(k.vk_netto),
              kosten_snapshot: Number(k.kosten_netto),
              zeit_min_snapshot: Number(k.zeit_min_je_einheit),
              lohnanteil_prozent: Number(k.lohnanteil_prozent),
            })}
          />
        </label>
        <button onClick={onRemove} className="pf-iconbtn" title="Position entfernen" aria-label="Position entfernen">×</button>
      </div>

      {/* Steht beim Kunden unter „Was wir dabei tun". Bis zum 28.09.2026 hieß
          das Feld „Notiz (Material, Besonderheit)" und lag versteckt unter
          „Kalkulation" — Benjamin fand es nicht und wunderte sich, woher die
          Texte beim Kunden kamen. Leer = die Beschreibung aus dem Katalog. */}
      {!nebenkosten && (
        <label className="pf-field pf-wz-feld pf-wz-oben-10">
          <span>Beschreibung für den Kunden</span>
          <input className="pf-input is-tight" value={p.material_notiz}
            onChange={e => onPatch({ material_notiz: e.target.value })}
            placeholder={katalogText || "Was bei einem Einsatz getan wird"} />
        </label>
      )}

      <div className="pf-wz-mengen">
        <label className="pf-field pf-wz-feld pf-wz-menge">
          <span>Menge</span>
          <input className="pf-input is-tight" type="number" inputMode="decimal" value={p.menge || ""}
            onChange={e => onPatch({ menge: Number(e.target.value) || 0 })} />
        </label>
        <label className="pf-field pf-wz-feld pf-wz-menge">
          <span>Einheit</span>
          {/* Zeigt „Stück", speichert `stueck` — der Datenwert bleibt stabil,
              der Mensch liest richtiges Deutsch. */}
          <input className="pf-input is-tight" value={einheitLabel(p.einheit)}
            onChange={e => onPatch({ einheit: einheitWert(e.target.value) })} placeholder="m²" />
        </label>
        <label className="pf-field pf-wz-feld pf-wz-menge">
          <span>€ je Einheit</span>
          <input className="pf-input is-tight" type="number" inputMode="decimal" value={p.einzelpreis_netto || ""}
            onChange={e => onPatch({ einzelpreis_netto: Number(e.target.value) || 0 })} />
        </label>
        <div className="pf-wz-durchgang">
          <div className="pf-wz-titel">{eur(jeDurchgang)}</div>
          <div className="pf-wz-mini">je Durchgang</div>
        </div>
      </div>

      {kannAufnehmen && (
        <div className="pf-wz-aufnehmen">
          <div className="pf-wz-aufnehmen-text">
            „{titel.trim()}" steht noch nicht im Katalog.
          </div>
          <button className="pf-btn is-small" disabled={aufnahmeLaeuft}
            onClick={async () => {
              setFehler("");
              try { await onKatalogAufnehmen(); }
              catch (e) { setFehler(fehlertext(e) || "Aufnehmen fehlgeschlagen."); }
            }}>
            {aufnahmeLaeuft ? "Nehme auf…" : "In den Katalog aufnehmen"}
          </button>
          {fehler && <div className="pf-wz-fehler pf-wz-oben-6">{fehler}</div>}
        </div>
      )}

      <div className={"pf-wz-jahr" + (gewaehlt ? "" : " is-offen")}>
        {gewaehlt
          ? `${gewaehlt}× im Jahr · ${eur(jeDurchgang * gewaehlt)}/Jahr`
          : "In welchen Monaten? Noch nichts gewählt."}
      </div>
      {nebenkosten && (
        <NebenkostenBezug p={p} positionen={positionen} katalog={katalog} onPatch={onPatch} />
      )}
      <div className="pf-monate">
        {/* Derselbe Rundlauf wie beim Kunden auf der Angebotsseite. Wie weit er
            geht, sagt der Katalog (Mig 187): beim Mähen kein Punkt → einer →
            zwei → keiner, sonst kein Punkt → einer → keiner. */}
        {MONAT_KURZ.map((m, i) => {
          const n = punkteIn(p, i + 1);
          return (
            <button key={m} className={"pf-monat" + (n ? " is-on" : "") + (n > 1 ? " is-doppelt" : "")}
              title={n === 0 ? `${m}: kein Einsatz` : n === 1 ? `${m}: ein Einsatz` : `${m}: zwei Einsätze`}
              disabled={rechnetSich}
              onClick={() => onPatch({ monate: umschalten(p.monate, i + 1, grenze) })}>
              {m}{n > 1 ? <sup>2</sup> : ""}
            </button>
          );
        })}
      </div>

      <button className="pf-linkbtn pf-wz-kalk-knopf" onClick={() => setDetails(d => !d)}>
        {details ? "Kalkulation ausblenden" : `Kalkulation · DB ${eur(db)} je ${einheitLabel(p.einheit)}`}
      </button>
      {details && (
        <>
          <div className="pf-grid-2 pf-wz-oben-8">
            <label className="pf-field pf-wz-feld"><span>Eigene Kosten € je Einheit</span>
              <input className="pf-input is-tight" type="number" inputMode="decimal" value={p.kosten_snapshot || ""}
                onChange={e => onPatch({ kosten_snapshot: Number(e.target.value) || 0 })} />
            </label>
            <label className="pf-field pf-wz-feld"><span>Minuten je Einheit</span>
              <input className="pf-input is-tight" type="number" inputMode="decimal" value={p.zeit_min_snapshot || ""}
                onChange={e => onPatch({ zeit_min_snapshot: Number(e.target.value) || 0 })} />
            </label>
            <label className="pf-field pf-wz-feld"><span>Lohnanteil % (§ 35a)</span>
              <input className="pf-input is-tight" type="number" inputMode="decimal" value={p.lohnanteil_prozent || ""}
                onChange={e => onPatch({ lohnanteil_prozent: Number(e.target.value) || 0 })} />
            </label>
          </div>
          {(p.kosten_snapshot === 0 || p.zeit_min_snapshot === 0) && (
            <div className="pf-wz-warn is-klein">
              Ohne Kosten und Minuten je Einheit stimmen Deckungsbeitrag und
              geplante Stunden für diese Position nicht.
            </div>
          )}
        </>
      )}
    </div>
  );
}
